/**
 * Marco backend seam.
 *
 * sendMessageStream() calls the real Marco chat endpoint (Flask SSE:
 * POST {SERVER}/api/v1/marco/chat). If that fails (offline / backend not running),
 * it transparently falls back to the local MOCK streamer so the UI keeps working.
 *
 * Same signature throughout: sendMessageStream(text, { onToken, onDone }) -> cancel().
 */
import {
  MARCO_CHAT_PATH,
  MARCO_KNOWLEDGE_PATH,
  MARCO_REPORTS_PATH,
} from 'components/upb_viewer_beta/copilot/constants';
import SERVER from 'lib/networking/endpoints';
import { httpGet, httpPost } from 'lib/networking/http';

const CHAT_URL = `${SERVER}${MARCO_CHAT_PATH}`;
const KNOWLEDGE_URL = `${SERVER}${MARCO_KNOWLEDGE_PATH}`;

/** Full URL of a wiki page (a self-contained, login-gated HTML doc opened in a new tab). */
export function wikiUrl(slug) {
  return `${KNOWLEDGE_URL}/${encodeURIComponent(slug)}`;
}

/** Fetch the wiki index for the Knowledge menu. Returns [] on any failure (best-effort). */
export async function fetchWikis() {
  try {
    const res = await fetch(KNOWLEDGE_URL, { credentials: 'include' });
    if (!res.ok) return [];
    const json = await res.json();
    return Array.isArray(json.wikis) ? json.wikis : [];
  } catch (_) {
    return [];
  }
}

/**
 * Kick off a shareable, self-hosted HTML report. Claude designs the page from the question, the
 * prose, the chart components, and the underlying query rows (datasets) - which can take 30-90s, so
 * the backend generates it off-request. Returns { id, url, status, expires_on } immediately with
 * status 'pending'; poll getReportStatus(id) until 'ready'. Throws on failure so the caller can
 * surface an inline error.
 */
export async function createReport(payload) {
  // httpPost wraps axios (withCredentials: true == credentials include, baseURL == SERVER) and
  // rejects on non-2xx, matching the previous raw-fetch contract (POST JSON, credentialed, throw
  // on failure, return the parsed body). Pass the relative path since axios prepends baseURL.
  const res = await httpPost(MARCO_REPORTS_PATH, {
    title: payload.title || 'Marco report',
    question: payload.question || '',
    narrative: payload.narrative || '',
    components: payload.components || [],
    datasets: payload.datasets || [],
  });
  // Build the shareable link from SERVER (the env-resolved public host) + id, not the backend's
  // url: behind the reverse proxy Flask sees an internal Host header (request.host_url) and returns
  // a non-routable host the browser refuses to load. SERVER is how every other call resolves.
  return { ...res.data, url: `${SERVER}${MARCO_REPORTS_PATH}/${res.data.id}` };
}

/** Poll a report's generation status. Returns { status }: pending | ready | invalid | expired. */
export async function getReportStatus(id) {
  const res = await httpGet(`${MARCO_REPORTS_PATH}/${id}/status`);
  return res.data;
}

/* ----------------------------- real backend (SSE) ----------------------------- */

function realStream(text, handlers, history, persona, attachment) {
  const controller = new AbortController();
  let stopped = false;
  // Once the user stops, swallow any further tokens/done. This covers both the real SSE stream
  // (aborted below) and the mock fallback, whose internal timer we can't reach from here.
  const guarded = {
    onToken: tok => {
      if (!stopped) handlers.onToken(tok);
    },
    onDone: meta => {
      if (!stopped) handlers.onDone(meta);
    },
  };
  // fire-and-forget; cancel via the returned fn
  runSse(text, guarded, controller, history, persona, attachment);
  return () => {
    stopped = true;
    controller.abort();
  };
}

async function runSse(text, { onToken, onDone }, controller, history, persona, attachment) {
  const traces = [];
  let components = [];
  let suggestions = [];
  let res;
  try {
    res = await fetch(CHAT_URL, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: text,
        history: history || [],
        persona: persona || 'business',
        attachment: attachment || null,
      }),
      signal: controller.signal,
    });
  } catch (e) {
    if (controller.signal.aborted) return; // user stopped — don't fall back to mock
    mockStream(text, { onToken, onDone });
    return;
  }
  if (!res.ok || !res.body) {
    mockStream(text, { onToken, onDone });
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let gotAnything = false;

  const handle = evt => {
    if (evt.type === 'token') {
      gotAnything = true;
      onToken(evt.text || '');
    } else if (evt.type === 'trace' && evt.trace) {
      traces.push(evt.trace);
    } else if (evt.type === 'ui' && Array.isArray(evt.components)) {
      components = evt.components;
    } else if (evt.type === 'suggest' && Array.isArray(evt.suggestions)) {
      suggestions = evt.suggestions;
    } else if (evt.type === 'error') {
      gotAnything = true;
      onToken(`\n\n⚠️ ${evt.error}`);
    }
  };

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop(); // keep trailing partial
      for (const part of parts) {
        const line = part.replace(/^data:\s?/, '').trim();
        if (!line) continue;
        try {
          handle(JSON.parse(line));
        } catch (_) {
          /* ignore keep-alive / malformed */
        }
      }
    }
  } catch (e) {
    if (controller.signal.aborted) return; // user stopped mid-stream — keep partial answer
    if (!gotAnything) {
      mockStream(text, { onToken, onDone });
      return;
    }
  }
  onDone({ trace: traces[0] || null, traces, components, suggestions });
}

/* --------------------------------- mock ---------------------------------------- */

// Mock answers mirror the real contract: prose + agent-declared `components` (the answer UI) +
// an optional collapsed `trace` (the raw query, shown under "Ran 1 lookup"). Note how the
// product-list answer renders as a DataTable, NOT a chart — the agent picks the right shape.
const CANNED = [
  {
    match: /per l0|by l0|category breakdown|products per cat|distribution/i,
    text:
      'Here is how canonical products break down across the top-level (L0) categories. ' +
      'Food dominates, followed by Operationals and Beverage.',
    trace: {
      tool: 'run_sql',
      detail: 'SELECT l0.category_name, COUNT(*) … GROUP BY l0.id;',
      rows: 4,
      primary: false,
    },
    components: [
      {
        component: 'BarChart',
        title: 'Products per L0 category',
        bars: [
          { label: 'Food', value: 1150420 },
          { label: 'Operationals', value: 477310 },
          { label: 'Beverage', value: 98140 },
          { label: 'Administrative', value: 27050 },
        ],
      },
    ],
    suggestions: ['Break Food down by L1 category', 'How many of these are unclassified?'],
  },
  {
    match: /unclassified|no l4|classif/i,
    text:
      'About 1,304,301 of 3,056,490 canonical products (42.7%) have no L4 category yet. ' +
      'The largest unclassified buckets sit under Food and Operationals.',
    trace: {
      tool: 'run_sql',
      detail: 'SELECT CASE WHEN l4_category_id IS NULL … GROUP BY status;',
      rows: 2,
      primary: false,
    },
    components: [
      {
        component: 'PieChart',
        title: 'Classified vs unclassified',
        slices: [
          { label: 'Classified', value: 1752189 },
          { label: 'Unclassified', value: 1304301 },
        ],
      },
    ],
  },
  {
    match: /similar|like|find products|dedup|glove|substitut/i,
    text: 'Closest matches by description embedding — suggestions, confirm before linking.',
    trace: {
      tool: 'vector_search',
      detail: 'semantic search (manufacturer) · "latex gloves large powdered"',
      rows: 4,
      primary: false,
    },
    components: [
      {
        component: 'DataTable',
        title: 'Similar products',
        columns: ['Product', 'Brand', 'Score'],
        rows: [
          { cells: ['Glove Latex Lg Pwdr', 'Handgards', 0.87] },
          { cells: ['Glove Latex Pwd Lg', 'Handgards', 0.86] },
          { cells: ['Glove Latex Lg Pwdr Free', 'Safeguard', 0.81] },
          { cells: ['Glove Latex Med Pwdr', 'Handgards', 0.74] },
        ],
      },
    ],
  },
  {
    match: /manufacturer|mpn|brand|who makes|made by|top/i,
    text:
      'Top manufacturers by number of MPNs. Kraft Heinz leads, with the rest dominated by ' +
      'foodservice equipment brands.',
    trace: {
      tool: 'run_sql',
      detail: 'SELECT gm.name, COUNT(gpm.id) … ORDER BY mpns DESC LIMIT 5;',
      rows: 5,
      primary: false,
    },
    components: [
      {
        component: 'BarChart',
        title: 'Top manufacturers by MPNs',
        bars: [
          { label: 'Kraft Heinz', value: 13585 },
          { label: 'Carlisle FoodService', value: 12660 },
          { label: 'Cambro', value: 10991 },
          { label: 'Steelite', value: 10349 },
          { label: 'Vollrath', value: 9095 },
        ],
      },
    ],
  },
];
const DEFAULT = {
  text:
    "I'm Marco (preview mode — backend not reachable). " +
    'Try: “how many products are unclassified?”, “top manufacturers by MPNs”, ' +
    '“products per L0 category”, or “find products like latex gloves”.',
  trace: null,
  components: null,
};

function mockStream(text, { onToken, onDone }) {
  const answer = CANNED.find(c => c.match.test(text)) || DEFAULT;
  const words = answer.text.split(' ');
  let i = 0;
  let cancelled = false;

  const start = setTimeout(function step() {
    if (cancelled) return;
    if (i < words.length) {
      onToken((i === 0 ? '' : ' ') + words[i]);
      i += 1;
      setTimeout(step, 28);
    } else {
      onDone({
        traces: answer.trace ? [answer.trace] : [],
        components: answer.components || [],
        suggestions: answer.suggestions || [],
      });
    }
  }, 350);

  return () => {
    cancelled = true;
    clearTimeout(start);
  };
}

/* --------------------------------- export -------------------------------------- */

export function sendMessageStream(text, handlers, history, persona, attachment) {
  return realStream(text, handlers, history, persona, attachment);
}
